<template>
  <CommunicationBubble
    v-if="correspondingRemarkId"
    ref="bubbleRef"
    :corresponding-remark-id="correspondingRemarkId"
    :linking-path="linkingPath"
    :position="'absolute'"
    :offset-right="offsetRight"
    :ordinal="ordinal[linkingPath]"
    type="liquid-template"
    data-behaviour="bubble-placeholder"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue'
import { useCurrentElement } from '@vueuse/core'

import { highlightClasses } from './constants'
import makeLiveInputBubbleHandler from '@app/application/communication/handlers/live-input-bubble-handler'
import CommunicationBubble from '@app/application/communication/sections/communication/components/communication-bubble.vue'
import { findLiveInputFromBubble } from '@app/application/communication/utils'

import { useBubbleStore } from '@app/application/communication/store/bubbles'

defineOptions({
  name: 'LiveInputBubble',
})

interface Props {
  linkingPath: string
  isPreviewMode?: boolean
}

const props = defineProps<Props>()

const currentElement = useCurrentElement<HTMLElement>()
const bubbleRef = ref<InstanceType<typeof CommunicationBubble> | null>(null)
const inputRef = ref<HTMLElement | null>(null)
const highlightApplied = ref(false)
const offsetRight = ref(0)

const bubbleStore = useBubbleStore()

const { highlightedPath, ordinal, remarkBubblesPerPath } =
  storeToRefs(bubbleStore)

bubbleStore.registerBubble('input', {
  linkingPath: props.linkingPath,
})

onBeforeUnmount(() => {
  bubbleStore.unregisterBubble('input', props.linkingPath)
  blurSiblingInput()
  inputRef.value = null
})

// Check if a corresponding remark bubble exists
const correspondingRemarkId = computed(
  () => remarkBubblesPerPath.value[props.linkingPath]?.id
)

watchEffect(() => {
  if (bubbleRef.value) {
    trackSiblingInput()
  }

  if (highlightedPath.value === props.linkingPath && bubbleRef.value) {
    highlightSiblingInput()
  } else {
    blurSiblingInput()
  }
})

watchEffect(() => {
  // Only try to wrap the sibling input if there is a corresponding remark bubble
  if (inputRef.value && bubbleRef.value) {
    wrapSibling()
    addOffset()
  }
})

function trackSiblingInput() {
  // Escape hatch into the DOM to find the input field
  const input = findLiveInputFromBubble(bubbleRef.value?.$el, props.linkingPath)

  if (!input) {
    return
  }

  inputRef.value = input
}

/**
 * In some cases, the input does not span the full length of the parent container.
 * The bubble should always be positioned at the right of the sibling input, not the parent container.
 */
function addOffset() {
  if (!bubbleRef.value || !inputRef.value || offsetRight.value) {
    return
  }

  const inputRect = inputRef.value.getBoundingClientRect()
  const bubbleRect = bubbleRef.value.$el.getBoundingClientRect()
  const offset = bubbleRect.left - inputRect.right + bubbleRect.width / 2

  offsetRight.value = inputRect.right < bubbleRect.left ? offset : 0
}

function wrapSibling() {
  if (!currentElement.value || !inputRef.value) {
    return
  }

  const parent = inputRef.value.parentNode as HTMLElement
  const vueContainer = currentElement.value.parentNode as HTMLElement

  if (!parent) {
    return
  }

  const handler = makeLiveInputBubbleHandler(
    inputRef.value,
    props.isPreviewMode
  )

  handler.insertWrapper(parent, [vueContainer, inputRef.value])
}

function highlightSiblingInput() {
  if (highlightApplied.value) {
    return
  }

  highlightApplied.value = true

  if (
    !inputRef.value ||
    inputRef.value?.classList.contains(highlightClasses[0])
  ) {
    return
  }

  inputRef.value.classList.add(...highlightClasses)
}

function blurSiblingInput() {
  if (!highlightApplied.value) {
    return
  }

  highlightApplied.value = false

  if (
    !inputRef.value ||
    !inputRef.value.classList.contains(highlightClasses[0])
  ) {
    return
  }

  inputRef.value.classList.remove(...highlightClasses)
}
</script>
